import { parseZonedDateTime, type ZonedDateTime } from '@internationalized/date';
import { DateTime, P } from '@piccolohealth/util';
import React from 'react';
import * as ChakraV3 from './chakra/index';

export const parseZonedDateTimeSafe = (date: DateTime): ZonedDateTime | null => {
  try {
    return parseZonedDateTime(date.toISO({ extendedZone: true }));
  } catch (_error) {
    return null;
  }
};

export const LOCALE = 'en-AU';

const Presets = () => {
  const presets = React.useMemo(() => {
    return [
      {
        label: 'Today',
        value: P.compact([
          parseZonedDateTimeSafe(DateTime.now().startOf('day')),
          parseZonedDateTimeSafe(DateTime.now().endOf('day')),
        ]),
      },
      {
        label: 'This week',
        value: P.compact([
          parseZonedDateTimeSafe(DateTime.now().startOf('week')),
          parseZonedDateTimeSafe(DateTime.now().endOf('week')),
        ]),
      },
      {
        label: 'Last week',
        value: P.compact([
          parseZonedDateTimeSafe(DateTime.now().minus({ weeks: 1 }).startOf('week')),
          parseZonedDateTimeSafe(DateTime.now().minus({ weeks: 1 }).endOf('week')),
        ]),
      },
      {
        label: 'This month',
        value: P.compact([
          parseZonedDateTimeSafe(DateTime.now().startOf('month')),
          parseZonedDateTimeSafe(DateTime.now().endOf('month')),
        ]),
      },
      {
        label: 'Last month',
        value: P.compact([
          parseZonedDateTimeSafe(DateTime.now().minus({ months: 1 }).startOf('month')),
          parseZonedDateTimeSafe(DateTime.now().minus({ months: 1 }).endOf('month')),
        ]),
      },
    ];
  }, []);

  return (
    <ChakraV3.HStack gap='1'>
      {presets.map(({ label, value }) => (
        <ChakraV3.DatePicker.PresetTrigger key={label} value={value} asChild>
          <ChakraV3.Button size='2xs' variant='subtle'>
            {label}
          </ChakraV3.Button>
        </ChakraV3.DatePicker.PresetTrigger>
      ))}
    </ChakraV3.HStack>
  );
};

interface Props extends Omit<ChakraV3.DatePicker.RootProps, 'onChange' | 'value'> {
  startDate: DateTime | null;
  endDate: DateTime | null;
  onStartDateChange: (date: DateTime | null) => void;
  onEndDateChange: (date: DateTime | null) => void;
  isDisabled?: boolean;
  isClearable?: boolean;
}

export const RangeDatepicker = (props: Props) => {
  const {
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    isDisabled,
    isClearable = true,
    ...rest
  } = props;

  const [pendingValue, setPendingValue] = React.useState<ChakraV3.DateValue[] | null>(null);

  const parentValue = React.useMemo(() => {
    return P.compact([
      props.startDate ? parseZonedDateTimeSafe(props.startDate) : undefined,
      props.endDate ? parseZonedDateTimeSafe(props.endDate) : undefined,
    ]);
  }, [props.startDate, props.endDate]);

  const [prevParentValue, setPrevParentValue] = React.useState(parentValue);

  // Discard any in-progress selection if the parent's dates change externally.
  if (prevParentValue !== parentValue) {
    setPrevParentValue(parentValue);
    setPendingValue(null);
  }

  const value = pendingValue ?? parentValue;

  const onChange = React.useCallback(
    (next: ChakraV3.DateValue[]) => {
      const [start, end] = next;

      // Cleared (e.g. via the ClearTrigger) — propagate null to the parent.
      if (!start && !end) {
        setPendingValue(null);
        props.onStartDateChange(null);
        props.onEndDateChange(null);
        return;
      }

      // Complete range — commit both dates to the parent and drop the buffer.
      if (start && end) {
        setPendingValue(null);
        props.onStartDateChange(DateTime.fromISO(start.toString()).startOf('day'));
        props.onEndDateChange(DateTime.fromISO(end.toString()).endOf('day'));
        return;
      }

      // Partial selection (first click of a range) — buffer locally so Chakra
      // still observes the [] -> [start] -> [start, end] transition for auto-close,
      // without clobbering the parent state mid-selection.
      setPendingValue(next);
    },
    [props],
  );

  return (
    <ChakraV3.DatePicker.Root
      value={value}
      onValueChange={(e) => onChange(e.value)}
      disabled={isDisabled}
      timeZone={startDate?.zoneName}
      numOfMonths={2}
      selectionMode='range'
      locale={LOCALE}
      openOnClick
      lazyMount
      unmountOnExit
      positioning={{ placement: 'bottom-start' }}
      {...rest}
    >
      <ChakraV3.DatePicker.Control>
        <ChakraV3.Group attached w='full'>
          <ChakraV3.DatePicker.Input
            index={0}
            data-pw='rangeDatepickerStartInput'
            placeholder='Start'
          />
          <ChakraV3.DatePicker.Input
            index={1}
            data-pw='rangeDatepickerEndInput'
            placeholder='End'
          />
        </ChakraV3.Group>
      </ChakraV3.DatePicker.Control>
      <ChakraV3.DatePicker.Positioner>
        <ChakraV3.DatePicker.Content>
          <ChakraV3.DatePicker.View view='day' colorPalette='purple'>
            <ChakraV3.DatePicker.Header />
            <ChakraV3.Flex gap='4'>
              <ChakraV3.DatePicker.DayTable />
              <ChakraV3.DatePicker.DayTable offset={1} />
            </ChakraV3.Flex>
          </ChakraV3.DatePicker.View>
          <ChakraV3.DatePicker.View view='month' colorPalette='purple'>
            <ChakraV3.DatePicker.Header />
            <ChakraV3.DatePicker.MonthTable />
          </ChakraV3.DatePicker.View>
          <ChakraV3.DatePicker.View view='year' colorPalette='purple'>
            <ChakraV3.DatePicker.Header />
            <ChakraV3.DatePicker.YearTable />
          </ChakraV3.DatePicker.View>
          <ChakraV3.HStack w='full'>
            {isClearable && <ChakraV3.DatePicker.ClearTrigger />}
            <ChakraV3.Spacer />
            <Presets />
          </ChakraV3.HStack>
        </ChakraV3.DatePicker.Content>
      </ChakraV3.DatePicker.Positioner>
    </ChakraV3.DatePicker.Root>
  );
};
